import React, { useEffect, useMemo, useState } from "react";
import NavBar from "../../../component/b2b/navBar/b2bNavBar";
import Sidebar from "../../../component/b2b/sideBar/b2bSideBar";
import { useMyContext } from "../../../context/MyContext";
import { Link, useNavigate } from "react-router-dom";
import {
  Base_Url,
  b2bdashboard,
  b2bAllShipmentList,
  shipmentlabel,
  cancleOrder,
  userdata,
  Url_For_View_Document
} from "../../../config/config";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from "recharts";

import {
  Search,
  ChevronLeft,
  ChevronRight,
  Download,
  Package,
  Truck,
  Wallet,
  AlertTriangle,
  Clock,
  Sun,
  Moon,
  Sunrise,
  Sunset,
  ShieldAlert,
  X
} from "lucide-react";

import { Button } from "@mui/material";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

export default function DashBoard() {
  const { isToggleSidebar } = useMyContext();
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const cusid = localStorage.getItem("cus_id");

  const [stats, setStats] = useState({});
  const [chartData, setChartData] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const rowsPerPage = 10;

  // ── KYC Pending Popup + Marquee logic ──
  const profile_flag = localStorage.getItem("profile_flag");
  const isKycPending = profile_flag === "0";
  const [showKycPopup, setShowKycPopup] = useState(false);

  const handlePageClick = () => {
    if (isKycPending) {
      setShowKycPopup(true);
    }
  };

  // ── Greeting + Live Clock state ──
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // ── Real user name (same source/logic as NavBar) ──
  const [userName, setUserName] = useState("User");

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser && storedUser.userData && storedUser.userData.name) {
      setUserName(storedUser.userData.name);
    }
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    const url = Base_Url + userdata;
    if (cusid) {
      try {
        const response = await fetch(url + "/" + cusid, {
          method: "GET",
          headers: {
            token: token,
            cus_id: cusid,
            "Content-Type": "application/json",
          },
        });
        const result = await response.json();
        if (result?.data?.name) {
          setUserName(result.data.name);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    }
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour >= 5 && hour < 12) {
      return { text: "Good Morning", Icon: Sunrise };
    } else if (hour >= 12 && hour < 17) {
      return { text: "Good Afternoon", Icon: Sun };
    } else if (hour >= 17 && hour < 21) {
      return { text: "Good Evening", Icon: Sunset };
    } else {
      return { text: "Good Night", Icon: Moon };
    }
  };

  const { text: greetingText, Icon: GreetingIcon } = getGreeting();

  const formattedTime = currentTime.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

  const formattedDate = currentTime.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  useEffect(() => {
    loadDashboard();
    loadOrders();
  }, []);

  const loadDashboard = async () => {
    const res = await fetch(`${Base_Url}${b2bdashboard}`, {
      headers: {
        token,
        cus_id: cusid,
        "Content-Type": "application/json"
      }
    });

    const data = await res.json();
    setStats(data?.data || {});
    setChartData(data?.chartData?.monthlyData || []);
  };

  const loadOrders = async () => {
    const res = await fetch(
      `${Base_Url}${b2bAllShipmentList}/Pending`,
      {
        headers: {
          token,
          cus_id: cusid,
          "Content-Type": "application/json"
        }
      }
    );

    const data = await res.json();
    setShipments(data?.data || []);
  };

  const cancelOrder = async (id) => {
    const res = await fetch(
      `${Base_Url}${cancleOrder}/${id}`,
      {
        headers: {
          token,
          cus_id: cusid,
          "Content-Type": "application/json"
        }
      }
    );

    if (res.ok) {
      toast.success("Cancelled");
      loadOrders();
    }
  };

  const handleDownload = async () => {
    const ids = shipments.map((x) => x.order_id);

    const res = await fetch(
      `${Base_Url}${shipmentlabel}`,
      {
        method: "POST",
        headers: {
          token,
          cus_id: cusid,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          order_ids: ids
        })
      }
    );

    const data = await res.json();

    if (data.status === "SUCCESS") {
      window.open(
        Url_For_View_Document + data.DownloadLink,
        "_blank"
      );
    }
  };

  const filtered = useMemo(() => {
    return shipments.filter((item) => {
      const customer =
        JSON.parse(item.dropoff_location || "{}")?.consignee_name || "";

      return (
        item.order_id?.toLowerCase().includes(search.toLowerCase()) ||
        customer.toLowerCase().includes(search.toLowerCase())
      );
    });
  }, [shipments, search]);

  const totalPages = Math.ceil(filtered.length / rowsPerPage);

  const rows = filtered.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const cards = [
    {
      title: "Orders",
      value: stats.total_order || 0,
      icon: Package,
      tint: "text-indigo-600 dark:text-indigo-300",
      ring: "bg-indigo-400/20"
    },
    {
      title: "Delivered",
      value: stats.total_delivered || 0,
      icon: Truck,
      tint: "text-emerald-600 dark:text-emerald-300",
      ring: "bg-emerald-400/20"
    },
    {
      title: "Revenue",
      value: stats.cod_remittance || "₹0",
      icon: Wallet,
      tint: "text-amber-600 dark:text-amber-300",
      ring: "bg-amber-400/20"
    },
    {
      title: "Issues",
      value: stats.total_rto || 0,
      icon: AlertTriangle,
      tint: "text-rose-600 dark:text-rose-300",
      ring: "bg-rose-400/20"
    }
  ];

  const pieData = [
    { name: "Delivered", value: stats.total_delivered || 0 },
    { name: "Pending", value: stats.total_order || 0 },
    { name: "RTO", value: stats.total_rto || 0 }
  ];


  const formatIndianCurrency = (value) => {
    if (!value) return "0";
    let num = parseFloat(value);
    if (num >= 10000000) {
      return (num / 10000000).toFixed(1).replace(/\.0$/, '') + "Cr";
    }
    else if (num >= 100000) {
      return (num / 100000).toFixed(1).replace(/\.0$/, '') + "L";
    }
    else if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '') + "K";
    }
    return num.toLocaleString('en-IN');
  };

  // ── shared glass classes (apple-glass, matches B2C palette) ──
  const glass =
    "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
    "border border-white/60 dark:border-white/10 " +
    "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";

  const miniStat =
    "rounded-lg border border-white/70 dark:border-white/10 bg-white/45 dark:bg-white/[0.05] " +
    "backdrop-blur-md px-2 py-1.5 shadow-lg";

  return (
    <div className="b2b-dashboard-page h-screen flex flex-col relative overflow-hidden bg-gradient-to-br from-slate-100 via-indigo-50 to-sky-100 dark:from-[#0b0f1a] dark:via-[#0d1220] dark:to-[#0a0e18] transition-colors">

      <div className="relative z-10 flex flex-col h-full">
        <NavBar />

        {/* ── KYC Pending Marquee ── */}
        {isKycPending && (
          <div className="w-full bg-red-600 text-white overflow-hidden py-1.5 shadow-md">
            <marquee behavior="scroll" direction="left" scrollamount="6">
              ⚠️ Your KYC verification is pending. Please complete your KYC to access all features. Click anywhere on the dashboard to update your KYC now.
            </marquee>
          </div>
        )}

        <div className="flex flex-1 overflow-hidden">

          {/* Sidebar always renders now — it collapses to an icon-only
              rail instead of disappearing when isToggleSidebar is false */}
          <div
            className={`${isToggleSidebar ? "w-64" : "w-20"} m-3 rounded-3xl ${glass} overflow-hidden transition-all duration-300 ease-in-out flex-shrink-0`}
            onClick={handlePageClick}
          >
            <Sidebar />
          </div>

          <div
            className="flex-1 overflow-auto p-2 sm:p-3"
            onClick={handlePageClick}
          >

            {/* ── Greeting + Live Clock ── */}
            <div className={`rounded-[15px] ${glass} mb-3 px-4 py-3 flex items-center justify-between flex-wrap gap-2`}>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-[13px] ring-1 ring-white/50 dark:ring-white/10 text-amber-500 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10">
                  <GreetingIcon size={20} strokeWidth={1.8} />
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-900 dark:text-white leading-tight">
                    {greetingText}, {userName}!
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-gray-400">
                    {formattedDate}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 rounded-full bg-white/40 dark:bg-white/5 border border-black/10 dark:border-white/10 px-3.5 py-1.5 backdrop-blur-md shadow-md">
                <Clock size={20} className="text-indigo-500 dark:text-indigo-300" />
                <span className="text-xl font-semibold tabular-nums text-slate-800 dark:text-gray-200">
                  {formattedTime}
                </span>
              </div>
            </div>

            {/* ── Stat cards (glassmorphic, matches B2C dashboard) ── */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {cards.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div
                    key={i}
                    className="
                      group
                      relative
                      min-w-0
                      overflow-hidden
                      rounded-[15px]
                      border border-t border-l
                      shadow-xl
                      border-white/60
                      dark:border-white/[0.08]
                      bg-gradient-to-br
                      from-white/80
                      via-white/55
                      to-white/35
                      dark:from-white/[0.09]
                      dark:via-white/[0.05]
                      dark:to-white/[0.02]
                      pl-2 pr-2 py-2
                      backdrop-blur-2xl
                      backdrop-saturate-150
                      transition-all
                      duration-300
                      hover:-translate-y-1
                      hover:border-white/80
                      dark:hover:border-white/[0.14]
                      hover:bg-gradient-to-br
                      hover:from-white/95
                      hover:via-white/70
                      hover:to-white/45
                      dark:hover:from-white/[0.13]
                      dark:hover:via-white/[0.07]
                      dark:hover:to-white/[0.03]
                      hover:shadow-[0_20px_50px_rgba(15,23,42,0.12)]
                    "
                  >
                    {/* GLASS HIGHLIGHT */}
                    <div
                      className="
                        pointer-events-none
                        absolute
                        -right-1
                        -top-8
                        h-20
                        w-20
                        rounded-full
                        bg-white/70
                        dark:bg-white/[0.12]
                        blur-2xl
                      "
                    />
                    {/* TOP SHEEN LINE — subtle Apple-glass inner edge */}
                    <div
                      className="
                        pointer-events-none
                        absolute
                        inset-x-0
                        top-0
                        h-px
                        bg-gradient-to-r
                        from-transparent
                        via-white/80
                        dark:via-white/20
                        to-transparent
                      "
                    />

                    {/* ICON */}
                    <div className="flex justify-between items-center relative">

                      <div
                        className={`
                        relative
                        flex
                        h-10
                        w-10
                        items-center
                        justify-center
                        rounded-[13px]
                        ring-1
                        ring-white/50
                        dark:ring-white/10
                        ${item.tint}
                      `}
                      >
                        <Icon size={20} strokeWidth={1.8} />
                      </div>

                      {/* TITLE */}
                      <div className="pr-2">
                        <div className="text-[10px] text-right font-medium text-slate-500 dark:text-gray-400">
                          {item.title}
                        </div>

                        {/* VALUE */}
                        <div className="text-[14px] text-right font-bold text-slate-900 dark:text-white">
                          {item.value}
                        </div>
                      </div>
                    </div>

                    {/* DETAILS (data untouched — only restyled) */}
                    <div className="relative mt-1 border-t border-dashed border-slate-300/70 dark:border-white/[0.12] pt-1 text-[10.5px] text-slate-500 dark:text-gray-400">
                      {i === 0 && (
                        <div>
                          <div className="flex justify-between px-2 items-center">
                            <div className="truncate text-[10px] font-medium text-slate-500 dark:text-gray-400">Delivered</div>
                            <div className="mt-0.5 text-xs font-semibold text-slate-800 dark:text-gray-200">{stats.total_delivered || 0}</div>
                          </div>
                          <div className="flex justify-between px-2 items-center">
                            <div className="truncate text-[10px] font-medium text-slate-500 dark:text-gray-400">RTO</div>
                            <div className="mt-0.5 text-xs font-semibold text-slate-800 dark:text-gray-200">{stats.total_rto || 0}</div>
                          </div>
                        </div>
                      )}
                      {i === 1 && (
                        <div className="flex justify-between px-2 items-center">
                          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            <span className="font-medium">Updated live</span>
                          </span>
                        </div>
                      )}
                      {i === 2 && (
                        <div className="flex justify-between px-2 items-center">
                          <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                            <span className="font-medium">Updated live</span>
                          </span>
                        </div>
                      )}
                      {i === 3 && (
                        <div className="flex justify-between px-2 items-center">
                          <span className="flex items-center gap-1 text-rose-600 dark:text-rose-400">
                            <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />
                            <span className="font-medium">Updated live</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

            </div>

                    <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 mt-2">
              <div className={`xl:col-span-2 rounded-3xl ${glass} p-4`}>

                <div className="flex justify-between mb-4">
                  <h2 className="font-semibold text-slate-800 dark:text-gray-100">Revenue Growth</h2>
                  <span className="text-[11px] shadow-md font-medium text-indigo-600 dark:text-indigo-300 bg-white/40 dark:bg-white/5 border border-white/50 dark:border-white/10 rounded-full px-3 py-1 backdrop-blur-md">Monthly</span>
                </div>

                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -32, bottom: -28 }}>
                      <defs>
                        <linearGradient id="flow" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#6366f1" stopOpacity={0.6} />
                          <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                        </linearGradient>
                      </defs>

                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={{ stroke: "rgba(148,163,184,0.3)" }} tickLine={false} />

                      <YAxis
                        tick={{ fontSize: 10, fill: "#94a3b8" }}
                        tickFormatter={formatIndianCurrency}
                        axisLine={false}
                        tickLine={false}
                      />

                      <Tooltip
                        contentStyle={{
                          borderRadius: 16,
                          border: "1px solid rgba(255,255,255,0.6)",
                          background: "rgba(255,255,255,0.7)",
                          backdropFilter: "blur(12px)",
                          boxShadow: "0 8px 32px rgba(31,41,55,0.12)",
                          fontSize: 12,
                        }}
                        formatter={(value) => [formatIndianCurrency(value), "Revenue"]}
                      />

                      <Area
                        dataKey="total_sales"
                        stroke="#4f46e5"
                        strokeWidth={2.5}
                        fill="url(#flow)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className={`rounded-3xl ${glass} p-4`}>

                <h2 className="font-semibold mb-4 text-slate-800 dark:text-gray-100">
                  Orders Status
                </h2>

                <div className="h-48">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        innerRadius={55}
                        outerRadius={90}
                      >
                        <Cell fill="#22c55e" />
                        <Cell fill="#6366f1" />
                        <Cell fill="#f43f5e" />
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          borderRadius: 16,
                          border: "1px solid rgba(255,255,255,0.6)",
                          background: "rgba(255,255,255,0.7)",
                          backdropFilter: "blur(12px)",
                          boxShadow: "0 8px 32px rgba(31,41,55,0.12)",
                          fontSize: 12,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

              </div>

              <div className={`rounded-3xl ${glass} p-4`}>

                <h2 className="font-semibold mb-4 text-slate-800 dark:text-gray-100">
                  Shipments
                </h2>

                <div className="h-48">
                  <ResponsiveContainer>
                    <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: -6 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={{ stroke: "rgba(148,163,184,0.3)" }} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 16,
                          border: "1px solid rgba(255,255,255,0.6)",
                          background: "rgba(255,255,255,0.7)",
                          backdropFilter: "blur(12px)",
                          boxShadow: "0 8px 32px rgba(31,41,55,0.12)",
                          fontSize: 12,
                        }}
                      />
                      <Bar
                        dataKey="total_sales"
                        radius={[6, 6, 0, 0]}
                        fill="#8b5cf6"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

              </div>

            </div>

            <div className={`rounded-3xl ${glass} mt-2 p-4 sm:p-4`}>

              <div className="flex justify-between items-center mb-1">

                <h2 className="text-lg font-semibold text-slate-800 dark:text-gray-100">
                  Orders List
                </h2>

                <Link
                  to="/b2b/shipments/list"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 shadow-md text-sm font-medium text-indigo-600 dark:text-indigo-300 hover:text-indigo-700 dark:hover:text-indigo-200 bg-white/40 dark:bg-white/5 hover:bg-white/60 dark:hover:bg-white/10 border border-white/50 dark:border-white/10 rounded-full px-3.5 py-1.5 backdrop-blur-md transition-colors"
                >
                  View All
                </Link>

              </div>

              <div className="relative mb-2">

                <Search
                  size={15}
                  className="absolute left-3 top-3 text-slate-400"
                />

                <input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Search order or customer..."
                  className={`w-full ${miniStat} pl-10 pr-3 py-2 text-sm outline-none focus:border-customPurple focus:ring-2 focus:ring-customPurple/20 dark:text-white dark:placeholder-slate-400`}
                />

              </div>

              <div className="rounded-2xl overflow-hidden backdrop-blur-xl bg-white/30 dark:bg-white/[0.03] border border-white/50 dark:border-white/10">
                <div className="overflow-auto custom-scrollbar">

                  <table className="w-full text-xs">

                    <thead>
                      <tr className="text-left text-slate-500 dark:text-gray-400 backdrop-blur-md bg-white/50 dark:bg-white/[0.06] border-b border-white/50 dark:border-white/10">
                        <th className="p-3 text-[10.5px] font-semibold uppercase tracking-wider">Order</th>
                        <th className="p-3 text-[10.5px] font-semibold uppercase tracking-wider">User</th>
                        <th className="p-3 text-[10.5px] font-semibold uppercase tracking-wider">Customer</th>
                        <th className="p-3 text-[10.5px] font-semibold uppercase tracking-wider">Amount</th>
                        <th className="p-3 text-[10.5px] font-semibold uppercase tracking-wider">Status</th>
                        <th className="p-3 text-[10.5px] font-semibold uppercase tracking-wider">Date</th>
                        <th className="p-3 text-[10.5px] font-semibold uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>

                    <tbody>

                      {rows.map((item, i) => {
                        const customer =
                          JSON.parse(item.dropoff_location || "{}");

                        return (
                          <tr
                            key={i}
                            className="border-b border-white/40 dark:border-white/10 hover:bg-purple-50/40 dark:hover:bg-white/5 transition-colors"
                          >
                            <td className="p-3 font-semibold text-blue-600 dark:text-blue-300">
                              {item.order_id}
                            </td>

                            <td className="p-3 text-slate-600 dark:text-gray-400">
                              {item.user_id}
                            </td>

                            <td className="p-3">
                              <p className="text-slate-800 dark:text-gray-200">{customer?.consignee_name}</p>
                              <p className="text-slate-400 dark:text-slate-500">
                                {customer?.phone}
                              </p>
                            </td>

                            <td className="p-3 text-slate-800 dark:text-gray-200">
                              ₹{item.cod_amount}
                            </td>

                            <td className="p-3">
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10.5px] font-semibold rounded-full backdrop-blur-md bg-blue-100/70 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                                <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                                {item.status}
                              </span>
                            </td>

                            <td className="p-3 text-slate-600 dark:text-gray-400">
                              {item.created_at?.split(" ")[0]}
                            </td>

                            <td className="p-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  cancelOrder(item.order_id);
                                }}
                                className="px-3 py-1 rounded-full bg-red-500/90 text-white backdrop-blur-md transition-colors hover:bg-red-600 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </td>

                          </tr>
                        );
                      })}

                    </tbody>

                  </table>

                </div>
              </div>

              <div className="flex justify-between items-center mt-4">

                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Page {page} of {totalPages || 1}
                </p>

                <div className="flex gap-2">

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPage(page - 1);
                    }}
                    disabled={page === 1}
                    className={`${miniStat} px-3 py-1 text-slate-600 dark:text-slate-300 transition-colors hover:border-customPurple hover:text-customPurple disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    <ChevronLeft size={14} />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setPage(page + 1);
                    }}
                    disabled={page === totalPages}
                    className={`${miniStat} px-3 py-1 text-slate-600 dark:text-slate-300 transition-colors hover:border-customPurple hover:text-customPurple disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    <ChevronRight size={14} />
                  </button>

                </div>

              </div>

            </div>

          </div>

        </div>

      </div>

      {/* ── KYC Pending Popup ── */}
      {isKycPending && showKycPopup && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          onClick={(e) => {
            e.stopPropagation();
            setShowKycPopup(false);
          }}
        >
          <div
            className="relative bg-white dark:bg-darkmodeColor rounded-2xl p-6 shadow-2xl max-w-sm w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowKycPopup(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:hover:text-white"
            >
              <X size={18} />
            </button>

            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/10 text-red-600 dark:text-red-400">
              <ShieldAlert size={28} />
            </div>

            <h2 className="text-lg font-bold text-slate-800 dark:text-white mb-2">
              KYC Verification Pending
            </h2>
            <p className="text-sm text-slate-500 dark:text-gray-400 mb-5">
              Your KYC is still pending. Please complete your KYC verification to continue using all features.
            </p>

            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowKycPopup(false)}
                className="px-4 py-2 rounded-full border border-gray-300 dark:border-white/10 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                Later
              </button>
              <button
                onClick={() => navigate("/updateKyc")}
                className="px-4 py-2 rounded-full bg-customPurple text-white text-sm font-medium hover:bg-purple-700 transition-colors"
              >
                Complete KYC
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}